import { offset } from '@floating-ui/react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { $isTableCellNode } from '@lexical/table';
import type { NodeKey } from 'lexical';
import { isDOMNode } from 'lexical';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useEditorActive } from '../../hooks/useEditorActive';
import { useNodeAnchoredFloating } from '../../hooks/useNodeAnchoredFloating';
import { useRootEvent } from '../../hooks/useRootEvent';
import { EditorPortal } from '../../ui/EditorPortal';
import { ToolbarMenu } from '../../ui/ToolbarMenu';
import { $getNodeOfType } from '../../utils/nodes';
import { TableActionMenuContent } from './TableActionMenuContent';
import type { ActiveCell } from './tableSelection';
import { $getCellFromDOM, $getSelectedCells } from './tableSelection';
import { useActiveTableCell } from './useActiveTableCell';

/**
 * Froala-style table popup: a chevron in the active cell's corner, or a right click, opens the cell menu.
 * - Shown only while the editor is editable and in use, like the main toolbar.
 */
export const TableActionMenuPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const editable = useLexicalEditable();
  const editorActive = useEditorActive();
  const active = useActiveTableCell();
  // Which cell's menu is open; a right click opens it before the selection catches up
  const [openFor, setOpenFor] = useState<NodeKey | null>(null);

  useRootEvent(
    'contextmenu',
    (event) => {
      if (!isDOMNode(event.target)) return;
      const target = event.target;
      const cellKey = editor.read(() => $getCellFromDOM(target)?.cell.getKey() ?? null);
      if (!cellKey) return;

      event.preventDefault();
      editor.update(() => {
        const cell = $getNodeOfType(cellKey, $isTableCellNode);
        // Keep a multi-cell selection that includes the clicked cell, so merge still works
        if (!cell || $getSelectedCells()?.cells.some((selected) => selected.is(cell))) return;
        cell.selectEnd();
      });
      setOpenFor(cellKey);
    },
    // Editable rather than active: a right click is often what focuses the editor
    editable,
  );

  if (!active || !editorActive || !editor.getElementByKey(active.cellKey)) return null;

  return (
    <CellMenuTrigger
      key={active.cellKey}
      active={active}
      open={openFor === active.cellKey}
      onOpenChange={(open) => setOpenFor(open ? active.cellKey : null)}
    />
  );
};

type CellMenuTriggerProps = { active: ActiveCell; open: boolean; onOpenChange: (open: boolean) => void };

/** The chevron, pinned just inside the cell's top-right corner, and the menu it opens. */
const CellMenuTrigger = ({ active, open, onOpenChange }: CellMenuTriggerProps) => {
  const { t } = useTranslation('common');
  const { refs, floatingStyles } = useNodeAnchoredFloating(active.cellKey, {
    placement: 'top-end',
    middleware: [offset({ mainAxis: -24, crossAxis: -4 })],
  });

  return (
    <EditorPortal ref={refs.setFloating} style={floatingStyles} className="z-popover">
      <ToolbarMenu icon={ChevronDown} caret="none" compact label={t('editor.table.options')} open={open} onOpenChange={onOpenChange}>
        {(close) => <TableActionMenuContent active={active} close={close} />}
      </ToolbarMenu>
    </EditorPortal>
  );
};
